import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserMenuDto, NavItem, MenuPermission } from '../models/menu.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly sessionKey = 'seamless_user_menus';

  private readonly menuTreeSignal = signal<UserMenuDto[]>(this.getStoredMenus());

  readonly menuTree = this.menuTreeSignal.asReadonly();

  // Computed signal to automatically filter and map fetched menus to standard sidebar NavItems
  readonly navItems = computed<NavItem[]>(() => {
    const tree = this.menuTree();
    const visibleTree = this.filterUserMenus(tree);
    return this.mapMenusToNavItems(visibleTree);
  });

  private getBaseUrl(): string {
    const api = environment.apiUrl;
    return api.endsWith('/') ? api : `${api}/`;
  }

  loadUserMenus(): Observable<UserMenuDto[]> {
    return this.http.get<any>(`${this.getBaseUrl()}menus/user-menus`).pipe(
      map((response) => {
        if (!response) return [];
        if (Array.isArray(response)) return response as UserMenuDto[];
        if (Array.isArray(response.data)) return response.data as UserMenuDto[];
        if (Array.isArray(response.menus)) return response.menus as UserMenuDto[];
        return [];
      }),
      tap((menus) => {
        if (menus) {
          this.setMenus(menus);
        }
      }),
      catchError((error) => {
        console.error('Failed to load user menus from backend:', error);
        // Fallback to stored session menus or empty array
        const stored = this.getStoredMenus();
        this.menuTreeSignal.set(stored);
        return of(stored);
      })
    );
  }

  clearMenus(): void {
    sessionStorage.removeItem(this.sessionKey);
    this.menuTreeSignal.set([]);
  }

  private setMenus(menus: UserMenuDto[]): void {
    sessionStorage.setItem(this.sessionKey, JSON.stringify(menus));
    this.menuTreeSignal.set(menus);
  }

  private getStoredMenus(): UserMenuDto[] {
    try {
      const data = sessionStorage.getItem(this.sessionKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  // Filter out hidden, inactive, denyAccess, or non-viewable menus
  private filterUserMenus(menus: UserMenuDto[]): UserMenuDto[] {
    if (!menus || menus.length === 0) return [];

    return menus
      .filter((menu) => {
        // Exclude hidden or inactive menus
        if (menu.hidden === true || menu.status === 'INACTIVE') {
          return false;
        }

        // Check permissions if provided
        if (menu.permissions) {
          if (menu.permissions.denyAccess === true) {
            return false;
          }
          if (menu.permissions.canView === false) {
            return false;
          }
        }

        return true;
      })
      .map((menu) => {
        const filteredChildren = menu.children && menu.children.length > 0
          ? this.filterUserMenus([...menu.children])
          : [];

        return {
          ...menu,
          children: filteredChildren
        };
      })
      .filter((menu) => {
        // If it's a folder (no route path) and all children were filtered out, omit the folder
        const hasPath = !!(menu.path && menu.path.trim());
        const hasChildren = menu.children && menu.children.length > 0;
        if (!hasPath && !hasChildren && menu.menuType === 'FOLDER') {
          return false;
        }
        return true;
      });
  }

  private mapMenusToNavItems(menus: UserMenuDto[]): NavItem[] {
    const sorted = [...menus].sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0));

    return sorted.map((menu) => {
      let route = menu.path || '';

      if (route && !route.startsWith('/')) {
        route = '/' + route;
      }

      if (route === '/tenants') {
        route = '/security/tenants';
      } else if (route === '/roles') {
        route = '/security/roles';
      } else if (route === '/roles/permissions') {
        route = '/security/roles/permissions';
      } else if (route === '/menus') {
        route = '/security/menus';
      } else if (route === '/users') {
        route = '/security/users';
      } else if (route === '/user-menus' || route === '/security/user-menus') {
        route = '/security/user-menus';
      } else if (route === '/services' || route === '/security/services') {
        route = '/security/services';
      } else if (route === '/modules' || route === '/security/modules') {
        route = '/security/modules';
      } else if (route === '/organizations' || route === '/security/organizations') {
        route = '/organizations';
      } else if (route === '/locations' || route === '/masters/locations') {
        route = '/locations';
      } else if (route === '/location-types' || route === '/masters/location-types') {
        route = '/location-types';
      } else if (route === '/financial-years' || route === '/masters/financial-years') {
        route = '/financial-years';
      } else if (route === '/series-masters' || route === '/masters/series-masters' || route === '/series') {
        route = '/series-masters';
      } else if (route === '/organization-services' || route === '/security/organization-services') {
        route = '/organization-services';
      } else if (route === '/organization-service-modules' || route === '/security/organization-service-modules') {
        route = '/organization-service-modules';
      } else if (route === '/service-modules' || route === '/security/service-modules') {
        route = '/service-modules';
      } else if (route === '/onboarding' || route === '/onboarding-wizard' || route === '/security/onboarding-wizard') {
        route = '/security/onboarding-wizard';
      } else if (route === '/item-categories' || route === '/shared/item-categories' || route === '/inventory/item-categories') {
        route = '/item-categories';
      }

      const icon = menu.icon || 'pi pi-chevron-right';

      const navItem: NavItem = {
        label: menu.name,
        icon: icon,
        route: route,
        permissions: menu.permissions,
        children: menu.children && menu.children.length > 0
          ? this.mapMenusToNavItems([...menu.children])
          : undefined
      };

      return navItem;
    });
  }

  /**
   * Dynamically resolves permissions for any given route URL using segment matching.
   * Zero hardcoding required — handles sub-routes (/users/new), route aliases, and module prefixes automatically.
   */
  getPermissionsForRoute(routePath: string): MenuPermission | null {
    if (!routePath) return null;

    // Clean URL parameters and trailing slashes
    const url = routePath.split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase();
    if (!url) return null;

    let bestMatchRouteLength = -1;
    let bestMatchPermissions: MenuPermission | null = null;

    const searchTree = (items: readonly NavItem[]) => {
      for (const item of items) {
        if (item.route && item.permissions) {
          const itemRoute = item.route.replace(/\/+$/, '').toLowerCase();

          // 1. Exact URL match (/security/users === /security/users)
          // 2. Sub-route match (/security/users/new starts with /security/users/)
          // 3. Path suffix match (/security/users ends with /users or /users ends with /security/users)
          const isExact = url === itemRoute;
          const isSubRoute = url.startsWith(itemRoute + '/');
          const isSuffix = (itemRoute.length > 1 && url.endsWith(itemRoute)) || (url.length > 1 && itemRoute.endsWith(url));

          if (isExact || isSubRoute || isSuffix) {
            // Select the longest matching route to ensure specific sub-routes take precedence over parent folders
            if (itemRoute.length > bestMatchRouteLength) {
              bestMatchRouteLength = itemRoute.length;
              bestMatchPermissions = item.permissions;
            }
          }
        }

        if (item.children && item.children.length > 0) {
          searchTree(item.children);
        }
      }
    };

    searchTree(this.navItems());
    return bestMatchPermissions;
  }

  hasPermission(routePath: string, action: keyof MenuPermission): boolean {
    const role = this.authService.currentUser()?.role?.toUpperCase();
    if (role === 'PLATFORM_HEAD' || role === 'PLATFROM_HEAD') {
      return true;
    }

    const perm = this.getPermissionsForRoute(routePath);
    if (!perm) {
      // Allow during initial app load before user menus arrive from API
      if (this.menuTree().length === 0) {
        return true;
      }
      return false; // Deny access for unlisted/unmatched routes once menus are loaded
    }
    return !!perm[action];
  }

  canCreate(routePath?: string): boolean {
    const path = routePath || window.location.pathname;
    return this.hasPermission(path, 'canCreate');
  }

  canEdit(routePath?: string): boolean {
    const path = routePath || window.location.pathname;
    return this.hasPermission(path, 'canEdit');
  }

  canDelete(routePath?: string): boolean {
    const path = routePath || window.location.pathname;
    return this.hasPermission(path, 'canDelete');
  }

  canApprove(routePath?: string): boolean {
    const path = routePath || window.location.pathname;
    return this.hasPermission(path, 'canApprove');
  }
}
